import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO.jsx';
import { 
  CheckCircle2, 
  Heart, 
  ShieldCheck, 
  Smartphone, 
  ArrowRight, 
  Info, 
  Eye
} from 'lucide-react';

function DonationFormContent() {
  const [selectedAmount, setSelectedAmount] = React.useState(500);
  const [customAmount, setCustomAmount] = React.useState('');
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [websiteUrl, setWebsiteUrl] = React.useState(''); // Honeypot
  const [status, setStatus] = React.useState({ type: '', message: '', amount: 0 });

  const presetAmounts = [100, 250, 500, 1000, 2500, 5000];

  const getActiveAmount = () => {
    if (selectedAmount === 'other') {
      return parseFloat(customAmount) || 0;
    }
    return selectedAmount;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalAmount = getActiveAmount();

    if (finalAmount <= 0) {
      setStatus({ type: 'error', message: 'Vælg venligst et gyldigt beløb at donere.' });
      return;
    }

    setStatus({ type: 'loading', message: 'Indsender...' });

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          form_type: 'donation',
          donation_type: 'once',
          amount: finalAmount,
          fullName,
          email,
          phone,
          website_url: websiteUrl
        })
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: `Tusind tak for din donation på ${finalAmount} kr.! Vi har modtaget din registrering.`,
          amount: finalAmount
        });
        setFullName('');
        setEmail('');
        setPhone('');
        setCustomAmount('');
        setSelectedAmount(500);
        setWebsiteUrl('');
      } else {
        const errData = await response.json().catch(() => ({}));
        setStatus({
          type: 'error',
          message: errData.message || 'Der skete en fejl under indsendelsen. Prøv venligst igen.'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.'
      });
    }
  };

  const activeAmount = getActiveAmount();

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-red-600 font-bold text-xs uppercase tracking-widest mb-1">
          <ShieldCheck size={16} />
          <span>Sikker donation</span>
        </div>
        <h2 className="text-3xl font-black text-slate-900 tracking-tight">Giv en donation</h2>
        <p className="text-slate-500 text-sm mt-2">
          Vælg et beløb for at støtte vores arbejde.
        </p>
      </div>

      {status.type === 'success' ? (
        <div className="space-y-6 my-6">
          <div className="p-6 bg-emerald-50 border border-emerald-100 rounded-2xl text-center">
            <div className="w-12 h-12 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg shadow-emerald-500/20">
              <CheckCircle2 size={24} />
            </div>
            <h4 className="text-lg font-bold text-emerald-900 mb-1">Donation registreret!</h4>
            <p className="text-emerald-700 text-sm leading-relaxed">
              Tusind tak for din vilje til at støtte. Vi har modtaget dine oplysninger.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-4">
            <h5 className="font-bold text-slate-900 text-sm uppercase tracking-wider text-center border-b border-slate-200 pb-2">
              Sådan fuldfører du din donation
            </h5>
            <p className="text-xs text-slate-600 text-center">
              Overfør dit valgte beløb på <strong className="text-slate-900">{status.amount} DKK</strong> via en af følgende muligheder:
            </p>

            <div className="space-y-3">
              {/* MobilePay Option */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm">
                <div>
                  <p className="text-xs font-bold text-slate-900">MobilePay</p>
                  <p className="text-[11px] text-slate-500">Overfør til vores nummer</p>
                </div>
                <div className="text-right">
                  <p className="text-base font-black text-red-600">#26545</p>
                </div>
              </div>

              {/* Bank Transfer Option */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-1 shadow-sm">
                <p className="text-xs font-bold text-slate-900">Bankoverførsel</p>
                <div className="grid grid-cols-2 text-xs text-slate-600 pt-1">
                  <div>Reg. nr.: <span className="font-semibold text-slate-900">9070</span></div>
                  <div>Kontonr.: <span className="font-semibold text-slate-900">1638202570</span></div>
                </div>
              </div>
            </div>

            <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-center">
              <p className="text-[11px] text-red-700 font-medium">
                <strong>VIGTIGT:</strong> Skriv venligst dit fulde navn eller e-mail som besked ved overførslen, så vi kan registrere din donation korrekt.
              </p>
            </div>
          </div>

          <button 
            type="button"
            onClick={() => setStatus({ type: '', message: '', amount: 0 })}
            className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-colors shadow-md cursor-pointer text-center"
          >
            Giv en ny donation
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input
              id="website_url"
              type="text"
              name="website_url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Vælg et beløb */}
          <div className="space-y-3">
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
              Vælg et beløb
            </label>
            <div className="grid grid-cols-3 gap-2">
              {presetAmounts.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomAmount('');
                  }}
                  className={`py-3 px-2 rounded-xl border text-center transition-all cursor-pointer font-bold text-sm flex flex-col items-center justify-center ${
                    selectedAmount === amount
                      ? 'border-red-500 bg-red-500 text-white shadow-md shadow-red-500/10'
                      : 'border-slate-200 hover:border-slate-300 bg-slate-50 text-slate-700'
                  }`}
                >
                  <span>{amount} kr</span>
                </button>
              ))}
            </div>

            {/* Custom Amount Input */}
            <div className="mt-3">
              <div className="relative">
                <input
                  type="number"
                  placeholder="Andet beløb (kr.)"
                  value={customAmount}
                  onChange={(e) => {
                    setSelectedAmount('other');
                    setCustomAmount(e.target.value);
                  }}
                  className={`w-full pl-4 pr-16 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-red-500 focus:bg-white transition-all text-sm shadow-sm font-semibold ${
                    selectedAmount === 'other' ? 'border-red-500 ring-1 ring-red-500' : 'border-slate-200'
                  }`}
                />
                <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                  <span className="text-xs font-bold text-slate-400 uppercase">
                    kr
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Kontaktoplysninger */}
          <div className="space-y-4 pt-2 border-t border-slate-100">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Kontaktoplysninger
            </h4>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                  Fulde navn *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Indtast dit fulde navn"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-red-500 focus:bg-white transition-all text-sm shadow-sm"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                  Email adresse *
                </label>
                <input
                  type="email"
                  required
                  placeholder="din@email.dk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-red-500 focus:bg-white transition-all text-sm shadow-sm"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                  Telefonnummer (valgfrit)
                </label>
                <input
                  type="tel"
                  placeholder="Indtast dit telefonnummer"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-red-500 focus:bg-white transition-all text-sm shadow-sm"
                />
              </div>
            </div>
          </div>

          {status.type === 'error' && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-xs text-red-600 font-medium text-center">{status.message}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status.type === 'loading' || activeAmount <= 0}
            className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-extrabold rounded-xl transition-all shadow-lg shadow-red-600/10 text-center cursor-pointer text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {status.type === 'loading' ? (
              <span>Indsender...</span>
            ) : (
              <>
                <span>
                  Donér {activeAmount > 0 ? `${activeAmount} kr.` : ''} nu
                </span>
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

function DonationCard() {
  return (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl p-6 md:p-10 relative overflow-hidden">
      {/* Decorative top bar */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-500 to-red-600" />
      
      <DonationFormContent />
    </div>
  );
}

export default function SupportUs() {
  const qrCode = "https://assets.auxodev.dk/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/qr-kode-takfordinindsats2326545-300x300.png";
  const supportLetter = "https://assets.auxodev.dk/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/63ad36ac-c7d3-4d76-b824-e3758ee689c4.png";

  return (
    <div className="pt-32 pb-24 bg-slate-50/50 min-h-screen">
      <SEO
        title="Støt sagen | Doner til Tak For Din Indsats"
        description="Støt dem, der passer på os. Giv et bidrag via MobilePay eller bankoverførsel til Tak For Din Indsats. Hver en krone gør en forskel."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-16 items-start mt-8 mb-24">
          
          {/* Left Column: Information & MobilePay */}
          <div className="lg:col-span-6 space-y-10">
            
            {/* Hero Text */}
            <div className="not-italic">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight leading-tight">
                Støt Sagen
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                Sig Tak For Din Indsats til dem, som dagligt redder Danmark, ved at sikre disse en rettidig og helhedsorienteret indsats.
              </p>
            </div>

            {/* Donation Value Propositions / Benefits */}
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="text-2xl font-black text-slate-900">Hvorfor donere?</h3>
                <p className="text-red-600 font-bold text-base">
                  Din donation går direkte til at hjælpe familier gennem kriser
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-red-50 text-red-600 flex items-center justify-center">
                    <Heart size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Direkte hjælp</h4>
                    <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                      Din donation går direkte til mennesker i nød.
                    </p>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-red-50 text-red-600 flex items-center justify-center">
                    <Eye size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Transparens</h4>
                    <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                      Vi rapporterer åbent om brugen af midler.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* MobilePay Section */}
            <div className="bg-white rounded-[2.5rem] p-6 md:p-8 border border-slate-100 shadow-xl flex flex-col md:flex-row gap-8 items-center">
              <div className="w-36 h-36 bg-slate-50 rounded-3xl shrink-0 border border-slate-100 overflow-hidden flex items-center justify-center">
                <img src={qrCode} alt="MobilePay QR" className="w-full h-full object-cover" />
              </div>
              <div className="space-y-4 text-center md:text-left">
                <div className="flex items-center gap-2 justify-center md:justify-start text-red-600 font-bold text-xs uppercase tracking-widest">
                  <Smartphone size={16} />
                  <span>Hurtig støtte</span>
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Giv et bidrag via MobilePay</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Den hurtigste måde at støtte os på. Du kan scanne QR-koden eller sende et valgfrit beløb direkte til vores nummer.
                </p>
                <div className="inline-block px-5 py-2.5 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-red-600 font-black text-lg tracking-wider">#26545</p>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: Secure Donation Form */}
          <div className="lg:col-span-6 lg:sticky lg:top-28">
            <DonationCard />
          </div>

        </div>

        {/* Opsigelse af fast donationsaftale */}
        <div className="mb-16 bg-white rounded-3xl border border-slate-200/90 p-6 md:p-8 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
              <Info size={24} />
            </div>
            <div className="space-y-1">
              <h3 className="text-base sm:text-lg font-bold text-slate-900">
                Opsigelse af fast donationsaftale
              </h3>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Ønsker du at opsige din faste donationsaftale, kan du gøre det direkte i din MobilePay-app eller ved at kontakte os på følgende mail{' '}
                <a 
                  href="mailto:info@takfordinindsats.dk" 
                  className="text-red-600 hover:text-red-700 font-bold underline underline-offset-2 transition-colors"
                >
                  info@takfordinindsats.dk
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Support Letter / Trust Section */}
        <div className="bg-slate-50 border border-slate-100 rounded-[3rem] p-6 md:p-16 text-slate-900 overflow-hidden relative">
          {/* Background decorative elements */}
          <div className="absolute -right-10 -bottom-10 w-96 h-96 bg-red-600/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -left-10 -top-10 w-96 h-96 bg-red-600/3 rounded-full blur-3xl pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
            <div className="lg:col-span-7 space-y-6 not-italic">
              <span className="text-xs font-bold uppercase tracking-widest text-red-600">Vores gennemsigtighed</span>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight text-slate-900">
                Hvorfor din støtte er afgørende
              </h2>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                Mange af de helte, der har ydet en indsats for os alle, falder igennem det offentlige sikkerhedsnet. Vi træder til, når ingen andre gør. Vi bruger alle indtægter på at skabe de bedste rammer for vores medlemmer.
              </p>
              <div className="pt-2">
                <Link 
                  to="/dokumenter-og-regnskab" 
                  className="inline-flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold transition-all shadow-lg shadow-red-600/10 hover:shadow-red-600/20"
                >
                  <ArrowRight size={20} /> 
                  <span>Se dokumenter og regnskab</span>
                </Link>
              </div>
            </div>
            
            <div className="lg:col-span-5 relative group">
              <div className="absolute inset-0 bg-red-600 rounded-3xl rotate-2 scale-105 opacity-10 transition-transform group-hover:rotate-1" />
              <img 
                src={supportLetter} 
                alt="Støtteerklæring" 
                className="relative rounded-3xl shadow-xl w-full border border-slate-100"
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
